import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  ClockCounterClockwise, Buildings, Warning, CheckCircle, XCircle,
  EnvelopeSimple, PaperPlaneTilt, MagnifyingGlass, Download, CalendarBlank,
  TrendUp, Hourglass, ArrowsClockwise,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const STATUS_CONFIG = {
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700', icon: XCircle, dotColor: 'bg-red-500' },
  due_soon: { label: 'Due Soon', color: 'bg-amber-100 text-amber-700', icon: Warning, dotColor: 'bg-amber-500' },
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, dotColor: 'bg-emerald-500' },
  no_plan: { label: 'No Plan', color: 'bg-slate-100 text-slate-600', icon: Hourglass, dotColor: 'bg-slate-400' },
};

const RenewalsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sending, setSending] = useState({});
  const [bulkSending, setBulkSending] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await adminApi.renewals();
      setData(res.data);
    } catch (e) { console.error(e); toast.error('Failed to load renewal data'); }
    finally { setLoading(false); }
  };

  const sendAlert = async (tenantId, hospitalName) => {
    setSending(s => ({ ...s, [tenantId]: true }));
    try {
      const res = await adminApi.sendRenewalAlert(tenantId);
      toast.success(res.data.message);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send alert'); }
    finally { setSending(s => ({ ...s, [tenantId]: false })); }
  };

  const sendBulkAlerts = async () => {
    if (!window.confirm('Send renewal alerts to ALL expired and due-soon hospitals?')) return;
    setBulkSending(true);
    try {
      const res = await adminApi.sendBulkAlerts();
      toast.success(res.data.message);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send bulk alerts'); }
    finally { setBulkSending(false); }
  };

  const exportExcel = async () => {
    try {
      const res = await adminApi.renewalReport();
      const ws = XLSX.utils.json_to_sheet(res.data.report);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Renewal Report');
      XLSX.writeFile(wb, `Renewal_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel report downloaded');
    } catch (e) { toast.error('Export failed'); }
  };

  const exportPdf = async () => {
    try {
      const res = await adminApi.renewalReport();
      const doc = new jsPDF('landscape');
      doc.setFontSize(16);
      doc.text('Hospital Renewal Report', 14, 15);
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, 14, 22);

      autoTable(doc, {
        startY: 28,
        head: [['Hospital', 'Plan', 'Start', 'End', 'Days Left', 'Status', 'Admin Email', 'Alerts Sent']],
        body: res.data.report.map(r => [
          r.hospital_name, r.plan, r.plan_start, r.plan_end,
          r.days_remaining, r.status, r.admin_email, r.alerts_sent,
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 23, 42] },
      });
      doc.save(`Renewal_Report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF report downloaded');
    } catch (e) { toast.error('Export failed'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" />
    </div>
  );

  const stats = data?.stats || {};
  const renewals = (data?.renewals || []).filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.name?.toLowerCase().includes(q) || r.admin_email?.toLowerCase().includes(q) || r.city?.toLowerCase().includes(q);
    }
    return true;
  });

  const dueCount = stats.expired + stats.due_soon;

  return (
    <div className="space-y-5" data-testid="renewals-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-rose-900 via-slate-900 to-slate-800 rounded-2xl p-6 text-white" data-testid="renewals-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold font-['Outfit']" data-testid="renewals-heading">Subscription Renewals</h1>
            <p className="text-sm text-slate-300 mt-0.5">Track hospital subscriptions, send renewal reminders & generate reports</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={exportExcel}
              className="h-8 text-xs gap-1.5 border-white/20 text-white hover:bg-white/10 bg-transparent" data-testid="export-excel-btn">
              <Download className="h-3.5 w-3.5" /> Excel
            </Button>
            <Button size="sm" variant="outline" onClick={exportPdf}
              className="h-8 text-xs gap-1.5 border-white/20 text-white hover:bg-white/10 bg-transparent" data-testid="export-pdf-btn">
              <Download className="h-3.5 w-3.5" /> PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: 'Total Hospitals', value: stats.total || 0, icon: Buildings, color: 'text-slate-700', bg: 'bg-slate-50 border-slate-200' },
          { label: 'Expired', value: stats.expired || 0, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
          { label: 'Due Soon (30d)', value: stats.due_soon || 0, icon: Warning, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
          { label: 'Active', value: stats.active || 0, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
          { label: 'Recently Renewed', value: stats.recently_renewed || 0, icon: TrendUp, color: 'text-sky-600', bg: 'bg-sky-50 border-sky-200' },
        ].map((s, i) => (
          <Card key={i} className={`${s.bg} border shadow-sm`}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`h-6 w-6 ${s.color} shrink-0`} weight="duotone" />
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">{s.label}</p>
                <p className={`text-xl font-bold font-['Outfit'] ${s.color}`}>{s.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Actions Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search hospital, email, city..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-9 border-slate-300" data-testid="renewal-search" />
        </div>
        <div className="flex gap-1">
          {['all', 'expired', 'due_soon', 'active', 'no_plan'].map(s => (
            <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'}
              onClick={() => setStatusFilter(s)}
              className={`h-8 text-xs ${statusFilter === s ? 'bg-slate-900 text-white' : 'border-slate-300'}`}
              data-testid={`filter-${s}`}>
              {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label}
            </Button>
          ))}
        </div>
        {dueCount > 0 && (
          <Button size="sm" onClick={sendBulkAlerts} disabled={bulkSending}
            className="h-8 bg-rose-600 hover:bg-rose-700 text-white text-xs gap-1.5 ml-auto" data-testid="bulk-alert-btn">
            <PaperPlaneTilt className="h-3.5 w-3.5" /> {bulkSending ? 'Sending...' : `Send Alerts to ${dueCount} Hospitals`}
          </Button>
        )}
      </div>

      {/* Renewals Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="renewals-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Hospital</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Plan</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Expiry Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Days Left</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Admin Contact</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Alerts Sent</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {renewals.map(r => {
                  const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.no_plan;
                  return (
                    <TableRow key={r.id} className={`hover:bg-sky-50/50 transition-colors ${r.status === 'expired' ? 'bg-red-50/30' : ''}`} data-testid={`renewal-row-${r.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${sc.dotColor} shrink-0`} />
                          <div>
                            <p className="font-medium text-slate-800 text-sm">{r.name}</p>
                            {r.city && <p className="text-[10px] text-slate-400">{r.city}</p>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px] capitalize">{r.plan}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {r.plan_end ? new Date(r.plan_end).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.days_remaining !== null ? (
                          <span className={`text-sm font-bold ${r.days_remaining < 0 ? 'text-red-600' : r.days_remaining <= 7 ? 'text-amber-600' : r.days_remaining <= 30 ? 'text-orange-500' : 'text-emerald-600'}`}>
                            {r.days_remaining < 0 ? `${Math.abs(r.days_remaining)}d overdue` : `${r.days_remaining}d`}
                          </span>
                        ) : <span className="text-xs text-slate-300">-</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${sc.color} border-0 text-[10px]`}>{sc.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          {r.admin_email && <p className="text-xs text-slate-600">{r.admin_email}</p>}
                          {r.admin_name && <p className="text-[10px] text-slate-400">{r.admin_name}</p>}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-sm font-medium text-slate-600">{r.alerts_sent_count || 0}</span>
                        {r.last_alert_sent && (
                          <p className="text-[9px] text-slate-400">Last: {new Date(r.last_alert_sent).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline"
                          onClick={() => sendAlert(r.id, r.name)}
                          disabled={sending[r.id] || !r.admin_email}
                          className="h-7 text-xs gap-1 border-rose-200 text-rose-600 hover:bg-rose-50"
                          data-testid={`send-alert-${r.id}`}>
                          <EnvelopeSimple className="h-3 w-3" />
                          {sending[r.id] ? 'Sending...' : 'Send Alert'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {renewals.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-slate-400">
                      <ClockCounterClockwise className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm">No hospitals match the current filter</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RenewalsPage;
